import { useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FaXmark } from "react-icons/fa6";

import { useAuth } from "../../context/auth/AuthContext";
import { GetEmployeeModel } from "../../services/Employee/EmployeeApi";

const UserMenu = () => {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const menuRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [showProfile, setShowProfile] = useState(false);

    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("login")) || {};
        } catch {
            return {};
        }
    }, []);

    const displayName = user.employeeName || user.fullName || "--";
    const displayRole = user.roleTypeName || user.roleName || "--";

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const initials = displayName && displayName !== "--"
        ? displayName
            .split(" ")
            .map((word) => word[0])
            .join("")
            .toUpperCase()
        : "EC";

    const handleLogout = () => {
        logout();
        setOpen(false);
        setShowProfile(false);
        navigate("/login");
    };

    return (
        <div className="relative" ref={menuRef}>
            <button
                onClick={() => setOpen((prev) => !prev)}
                className="profile-trigger flex items-center rounded-2xl border border-border bg-white p-1.5 shadow-sm transition hover:-translate-y-[1px] hover:border-primary hover:shadow-md"
            >
                <div className="relative flex h-8 w-8 items-center justify-center rounded-[14px] bg-gradient-to-br from-primary to-secondary text-[11px] font-bold text-white shadow-md shadow-blue-100">
                    {initials}
                    <span className="profile-trigger__status absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-400" />
                </div>
            </button>

            {open && (
                <div className="profile-menu profile-menu-popover absolute right-0 z-30 mt-3 w-48 rounded-2xl border border-border bg-white p-2 shadow-xl">
                    <button
                        onClick={() => {
                            setShowProfile(true);
                            setOpen(false);
                        }}
                        className="block w-full rounded-xl px-4 py-2 text-left text-sm hover:bg-background-muted hover:text-primary transition"
                    >
                        View Profile
                    </button>

                    <button
                        onClick={handleLogout}
                        className="block w-full rounded-xl px-4 py-2 text-left text-sm hover:bg-background-muted hover:text-primary transition"
                    >
                        Logout
                    </button>
                </div>
            )}

            {showProfile && (
                <ProfileModal
                    user={user}
                    displayName={displayName}
                    displayRole={displayRole}
                    initials={initials}
                    onClose={() => setShowProfile(false)}
                    onLogout={handleLogout}
                />
            )}
        </div>
    );
};

export default UserMenu;

const ProfileModal = ({ user, displayName, displayRole, initials, onClose, onLogout }) => {
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        const employeeKeyID =
            user.employeeKeyID ||
            user.employeeKeyId ||
            user.EmployeeKeyID ||
            user.userKeyID ||
            user.userKeyId ||
            user.UserKeyID;

        if (!employeeKeyID) {
            setLoading(false);
            return () => {
                active = false;
            };
        }

        const loadProfile = async () => {
            try {
                const response = await GetEmployeeModel(employeeKeyID);
                if (!active) return;

                if (response?.statusCode === 200) {
                    setEmployee(response?.responseData?.data || null);
                }
            } catch {
                // Keep displaying the login data if profile loading fails.
            } finally {
                if (active) setLoading(false);
            }
        };

        loadProfile();
        return () => {
            active = false;
        };
    }, [user]);

    const profileName = employee
        ? [employee.firstName, employee.lastName].filter(Boolean).join(" ") || displayName
        : displayName;
    const profileInitials = profileName && profileName !== "--"
        ? profileName.split(" ").map((word) => word[0]).join("").toUpperCase()
        : initials;

    return createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-5">
            <div className="absolute inset-0 bg-black/25 backdrop-blur-[6px]" onClick={onClose} />

            <div className="profile-modal profile-modal-panel app-modal__panel relative mx-auto w-full max-w-sm">
                <button
                    onClick={onClose}
                    className="profile-modal-close absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-white text-slate-400 transition hover:border-primary hover:text-text-primary"
                    aria-label="Close profile modal"
                >
                    <FaXmark className="app-icon text-lg" />
                </button>

                <div className="mb-6 flex flex-col items-center">
                    <div className="flex h-20 w-20 items-center justify-center rounded-[22px] bg-primary text-2xl font-bold text-white">
                        {profileInitials}
                    </div>

                    <h2 className="mt-3 text-lg font-semibold text-gray-800">
                        {profileName}
                    </h2>

                    <span className="text-sm text-gray-500">
                        {employee?.designationName || displayRole}
                    </span>
                </div>

                <div className="space-y-3 text-sm">
                    {loading && <p className="py-2 text-center text-gray-500">Loading profile...</p>}
                    <InfoRow label="Mobile" value={employee?.mobileNumber || user.mobileNumber || user.mobileNo} />
                    <InfoRow label="Email" value={employee?.emailID || user.emailID} />
                    <InfoRow label="Designation" value={employee?.designationName || displayRole} />
                    <InfoRow label="Address" value={employee?.address || user.address} />
                    <InfoRow
                        label="Status"
                        value={
                            <span
                                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${user.status
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                    }`}
                            >
                                {user.status ? "Active" : "Inactive"}
                            </span>
                        }
                    />
                </div>

                <button
                    onClick={onLogout}
                    className="btn-brand mt-6 w-full px-5 py-3 font-semibold"
                >
                    Logout
                </button>
            </div>
        </div>,
        document.body
    );
};

const InfoRow = ({ label, value }) => (
    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
        <span className="text-gray-500">{label}</span>
        <span className="font-medium text-gray-800">{value || "--"}</span>
    </div>
);
